import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import i18n from './i18n';
import { notifications, errorToastShownRecently } from './utils/notifications';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ToastProvider } from './components/ui/Toast';
import { NotificationInitializer } from './components/NotificationInitializer';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SkeletonCard } from './components/ui/Skeleton';
import { AuthProvider, useAuth } from './components/auth/AuthProvider';
import { AppBootstrapGate } from './components/auth/AppBootstrapGate';
import { LanguageRedirect } from './components/routing/LanguageRedirect';
import { LangParamSync } from './components/routing/LangParamSync';
import { MandateRouteTransition } from './components/routing/MandateRouteTransition';
import { useSyncUserLanguage } from './hooks/useSyncUserLanguage';

const SignIn = lazy(() => import('./pages/auth/SignIn'));

// Lazy load pages for code splitting
const Entities = lazy(() => import('./pages/entities/Entities'));
const EntityPage = lazy(() => import('./pages/entities/EntityPage'));
const BankAccounts = lazy(() => import('./pages/bank-accounts/BankAccounts'));
const BankAccountPage = lazy(() => import('./pages/bank-accounts/BankAccountPage'));
const Signatories = lazy(() => import('./pages/signatories/Signatories'));
const SignatoryPage = lazy(() => import('./pages/signatories/SignatoryPage'));
const Banks = lazy(() => import('./pages/banks/Banks'));
const Users = lazy(() => import('./pages/users/Users'));
const MandateHistoryPage = lazy(() => import('./pages/mandate-history/MandateHistoryPage'));
const WorkflowsPage = lazy(() => import('./pages/workflows/WorkflowsPage'));
const Ebam = lazy(() => import('./pages/ebam/Ebam'));
const WorkflowConfig = lazy(() => import('./pages/settings/WorkflowConfig').then(m => ({ default: m.WorkflowConfig })));
const MandateDetails = lazy(() => import('./pages/mandates/MandateDetails'));
const NewMandatePage = lazy(() => import('./pages/mandates/NewMandatePage'));
const MandateTemplates = lazy(() => import('./pages/settings/MandateTemplates'));
const OrganisationSettings = lazy(() => import('./pages/settings/OrganisationSettings'));
const PrintableMandate = lazy(() => import('./pages/print/PrintableMandate'));
const HomePage = lazy(() => import('./pages/home/HomePage'));
const TaskActionView = lazy(() => import('./pages/home/TaskActionView'));
const Reviews = lazy(() => import('./pages/reviews/Reviews'));
const ReviewCampaignPage = lazy(() => import('./pages/reviews/ReviewCampaignPage'));
const ReviewEntityPage = lazy(() => import('./pages/reviews/ReviewEntityPage'));
const KycRequests = lazy(() => import('./pages/kyc/KycRequests'));
const KycRequestPage = lazy(() => import('./pages/kyc/KycRequestPage'));
const DesignSystem = lazy(() => import('./pages/design-system/DesignSystem'));
const HelpPage = lazy(() => import('./pages/help/HelpPage'));

// Prospect microsites (single-prospect personalized pages)
const DubreuilMicrosite = lazy(() => import('./pages/microsite/DubreuilMicrosite'));
const ExpleoMicrosite = lazy(() => import('./pages/microsite/ExpleoMicrosite'));

// Legal pages (public)
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const Legal = lazy(() => import('./pages/legal/Legal'));
const Contact = lazy(() => import('./pages/legal/Contact'));

// Loading component for suspense
function PageLoader() {
  return (
    <div style={{ padding: '24px' }}>
      <SkeletonCard />
    </div>
  );
}

// Create a client
const queryClient = new QueryClient({
  // Safety net: surface mutation failures that nothing else reports.
  // Hooks with their own onError are skipped; call sites using
  // try/catch + toast get a short head start, then the recent-error
  // check prevents a duplicate toast.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      setTimeout(() => {
        if (errorToastShownRecently()) return;
        const message = error instanceof Error && error.message ? error.message : undefined;
        notifications.show({
          title: i18n.t('toast.error.title', { ns: 'common' }),
          message: message ?? i18n.t('toast.error.message', { ns: 'common' }),
          color: 'red'
        });
      }, 250);
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 3, // Retry failed requests 3 times before giving up
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s (max 30s)
      refetchOnWindowFocus: false
    }
  }
});

// Public marketing microsites: render outside the app Layout, regardless of auth state.
// noindex/nofollow is set per-page in DubreuilMicrosite so search engines don't surface
// the slug. The clean /dubreuil URL is intentional: easier to share by email.
const DUBREUIL_PATH = '/dubreuil';
const EXPLEO_PATH = '/expleo';
const PUBLIC_MICROSITE_PATHS = [DUBREUIL_PATH, EXPLEO_PATH];

// Protected app routes that require authentication
function ProtectedApp() {
  const location = useLocation();
  useSyncUserLanguage();

  // Marketing microsites bypass the app shell even when the user is signed in.
  if (PUBLIC_MICROSITE_PATHS.includes(location.pathname)) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path={DUBREUIL_PATH} element={<DubreuilMicrosite />} />
          <Route path={EXPLEO_PATH} element={<ExpleoMicrosite />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AppBootstrapGate>
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/home/:taskType" element={<TaskActionView />} />
            {/* Legacy redirects for bookmarks */}
            <Route path="/dashboard" element={<Navigate to="/home" replace />} />
            <Route path="/inbox" element={<Navigate to="/home" replace />} />
            <Route path="/entities" element={<Entities />} />
            <Route path="/entities/new" element={<EntityPage />} />
            <Route path="/entities/:entityCode" element={<EntityPage />} />
            <Route path="/bank-accounts" element={<BankAccounts />} />
            <Route path="/bank-accounts/new" element={<BankAccountPage />} />
            <Route path="/bank-accounts/:id" element={<BankAccountPage />} />
            <Route path="/signatories" element={<Signatories />} />
            <Route path="/signatories/new" element={<SignatoryPage />} />
            <Route path="/signatories/:id" element={<SignatoryPage />} />
            <Route path="/kyc" element={<KycRequests />} />
            <Route path="/kyc/:id" element={<KycRequestPage />} />
            <Route path="/banks" element={<Banks />} />
            <Route path="/users" element={<Users />} />
            <Route path="/mandate-history" element={<MandateHistoryPage />} />
            <Route path="/workflows" element={<WorkflowsPage />} />
            <Route path="/workflows/new" element={<WorkflowConfig />} />
            <Route path="/workflows/:id" element={<WorkflowConfig />} />
            <Route path="/ebam" element={<Ebam />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/reviews/:id" element={<ReviewCampaignPage />} />
            <Route path="/reviews/:id/entities/:entityId" element={<ReviewEntityPage />} />
            <Route path="/settings/mandate-templates" element={<MandateTemplates />} />
            <Route path="/settings/organisation" element={<OrganisationSettings />} />
            <Route path="/mandates/new" element={<NewMandatePage />} />
            <Route path="/mandates/:id" element={<MandateRouteTransition><MandateDetails /></MandateRouteTransition>} />
            <Route path="/design-system" element={<DesignSystem />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/help/:slug" element={<HelpPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </AppBootstrapGate>
  );
}

// Print route is consumed by the backend Puppeteer renderer. It must work
// without any session: the only authentication is the signed token validated
// server-side. Handle it BEFORE the AuthGate logic so we never redirect to
// the sign-in flow.
function PrintRouteCheck() {
  const location = useLocation();
  if (location.pathname.startsWith('/print/mandate/')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/print/mandate/:id" element={<PrintableMandate />} />
        </Routes>
      </Suspense>
    );
  }
  return null;
}

function AuthGate() {
  const location = useLocation();
  const { isSignedIn } = useAuth();

  // Bypass auth for print routes: token-based auth replaces the session.
  // Must come AFTER all hook calls so the hook order stays stable.
  if (location.pathname.startsWith('/print/mandate/')) {
    return <PrintRouteCheck />;
  }

  if (isSignedIn) {
    return <ProtectedApp />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        <Route path="/login" element={<SignIn />} />

        <Route path="/:lang" element={<LangParamSync />}>
          <Route index element={<Navigate to="/login" replace />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route path="legal" element={<Legal />} />
          <Route path="contact" element={<Contact />} />
        </Route>

        <Route path="/privacy" element={<LanguageRedirect />} />
        <Route path="/terms" element={<LanguageRedirect />} />
        <Route path="/legal" element={<LanguageRedirect />} />
        <Route path="/contact" element={<LanguageRedirect />} />

        <Route path={DUBREUIL_PATH} element={<DubreuilMicrosite />} />
        <Route path={EXPLEO_PATH} element={<ExpleoMicrosite />} />

        <Route path="/*" element={<LanguageRedirect />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <NotificationInitializer />
          <AuthProvider>
            <Router>
              <AuthGate />
            </Router>
          </AuthProvider>
        </ToastProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;